/**
 * useMapperCatalog  (SM-21 / DA-2294)
 *
 * Fetches the mapper grammar catalog (SM-20 — GET /survey/getMapperCatalog) that the
 * cascading builders resolve their step options against. Cached at module scope so
 * every MapperBuilderDialog instance opened during the form session shares one fetch
 * (DA-2293 acceptance: "one catalog fetch per form session"), instead of re-fetching
 * per choice / per dialog open.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { RunContextApp } from 'framework/RCApp'
import { SurveyQBApis } from 'apis/surveyQBAPI'
import { MapperCatalog } from 'types/apis/surveyInterfaces'

let catalogPromise: Promise<MapperCatalog> | null = null

function fetchCatalog(rc: RunContextApp): Promise<MapperCatalog> {
  if (!catalogPromise) {
    catalogPromise = SurveyQBApis.getMapperCatalog(rc).then((resp) => {
      if (resp.error || !resp.data) {
        catalogPromise = null // let the next attempt retry instead of caching a failure
        throw new Error(resp.error?.message || 'Failed to load mapper catalog')
      }
      // A 200 carrying the wrong envelope is not a success, and truthiness can't tell the
      // two apart: an extra layer of wrapping leaves `resp.data` set but every catalog
      // field undefined, which the pickers render as an empty dropdown — indistinguishable
      // from "this pilot has no attributes configured". Fail loudly on the shape instead.
      if (!Array.isArray(resp.data.homeProfileAttributes)) {
        catalogPromise = null
        throw new Error('Mapper catalog response was not in the expected shape')
      }
      return resp.data
    })
  }
  return catalogPromise
}

export interface UseMapperCatalogState {
  catalog?: MapperCatalog
  loading: boolean
  error?: string
}

export function useMapperCatalog(_rc: RunContextApp, accountType?: string): UseMapperCatalogState {
  const [state, setState] = useState<UseMapperCatalogState>({ loading: true })

  // Held in a ref rather than an effect dependency on purpose. The catalog is global
  // and cached at module scope, so there is nothing to refetch when `rc` changes — and
  // depending on its identity means any caller whose context value isn't memoized
  // re-runs the effect on every render, each run setting a fresh state object and
  // triggering the next render. That's an infinite loop, and it is not the caller's
  // job to know that.
  const rcRef = useRef(_rc)
  rcRef.current = _rc

  useEffect(() => {
    let cancelled = false

    fetchCatalog(rcRef.current)
      .then((catalog) => {
        if (!cancelled) setState({ catalog, loading: false })
      })
      .catch((err) => {
        if (!cancelled) setState({ loading: false, error: err?.message || 'Failed to load mapper catalog' })
      })

    return () => { cancelled = true }
  }, [])

  // DA-2184: HP attributes are account-type-scoped (the seed tags e.g. `propertyType`/dwelling
  // RESIDENTIAL, `employees`/numOccupants SMB). Filter to the pilot's account type so a question
  // never offers the other segment's attributes — and, critically, so a shared column like
  // `numOccupants` (occupants D vs employees C) resolves to one segment's values, not both.
  // An attribute with no accountType applies to all; no `accountType` arg means no filtering.
  const catalog = useMemo<MapperCatalog | undefined>(() => {
    if (!state.catalog || !accountType) return state.catalog
    return {
      ...state.catalog,
      homeProfileAttributes: (state.catalog.homeProfileAttributes ?? []).filter(
        (a) => !a.accountType?.length || a.accountType.includes(accountType),
      ),
    }
  }, [state.catalog, accountType])

  return { ...state, catalog }
}
